import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const MIN_LENGTH = 10;

export default function AdminAccount() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canSubmit =
        current.length > 0 &&
        next.length >= MIN_LENGTH &&
        next === confirm &&
        next !== current &&
        !saving;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (next.length < MIN_LENGTH) {
            setError(`New password must be at least ${MIN_LENGTH} characters.`);
            return;
        }
        if (next !== confirm) {
            setError("New password and confirmation do not match.");
            return;
        }
        if (next === current) {
            setError("New password must be different from the current password.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated. Use it next time you sign in.");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Could not update password";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-10 max-w-2xl" data-testid="admin-account">
            <h1 className="font-serif text-3xl text-brand-ink mb-2">Account</h1>
            <p className="text-sm text-brand-muted mb-8">
                Signed in as <span className="text-brand-ink">{user?.email}</span>. Change your admin password below — the new password takes effect immediately.
            </p>

            <section className="border border-brand-line p-6 bg-brand-paper">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-4">
                    Change password
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
                    <PasswordField
                        label="Current password"
                        value={current}
                        onChange={setCurrent}
                        testid="current-password-input"
                        autoComplete="current-password"
                    />
                    <PasswordField
                        label={`New password (min. ${MIN_LENGTH} chars)`}
                        value={next}
                        onChange={setNext}
                        testid="new-password-input"
                        autoComplete="new-password"
                    />
                    <PasswordField
                        label="Confirm new password"
                        value={confirm}
                        onChange={setConfirm}
                        testid="confirm-password-input"
                        autoComplete="new-password"
                    />
                    {error && (
                        <div className="text-sm text-red-700" data-testid="change-password-error">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="change-password-submit"
                            className="text-xs uppercase tracking-wider px-5 py-2.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? "Saving…" : "Update password"}
                        </button>
                        <p className="text-xs text-brand-muted">
                            Use a long passphrase. Existing sessions stay signed in until they expire.
                        </p>
                    </div>
                </form>
            </section>
        </div>
    );
}

function PasswordField({ label, value, onChange, testid, autoComplete }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-brand-muted">{label}</span>
            <input
                type="password"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                data-testid={testid}
                className="mt-2 w-full bg-transparent border-b border-brand-line p-2 text-brand-ink focus:outline-none focus:border-brand-ink"
            />
        </label>
    );
}
